<template>
  <l-map
    ref="map"
    :key="geolocUserTimestamp"
    :zoom="zoom"
    :center="center"
    :options="mapOptions"
    @ready="onMapReady"
    @update:zoom="zoomUpdate"
    @update:bounds="boundsUpdate"
    @popupopen="onPopupOpen($event)"
    @popupclose="onPopupClose($event)"
  >
    <l-tile-layer :url="url" :attribution="attribution" />
    <template v-if="!shapesLoading">
      <l-polyline
        class="polylineee"
        v-for="polyline in polylines"
        :lat-lngs="polyline._latlngs"
        :color="polyline.options.color"
      />
    </template>
    <l-marker
      v-if="pedestrianDistance.image != ''"
      id="pedestrianDistance"
      :z-index-offset="-1000"
      :lat-lng="center"
      :options="pedestrianDistance.options"
    >
      <l-icon
        :icon-size="pedestrianDistance.size"
        :icon-url="pedestrianDistance.image"
      >
      </l-icon>
    </l-marker>

    <l-marker
      v-if="userPosition"
      :lat-lng="userPosition"
      :z-index-offset="1000"
    >
      <l-icon :icon-size="[40, 40]">
        <mapGeolocMarkerSVG></mapGeolocMarkerSVG>
      </l-icon>
    </l-marker>

    <l-feature-group
      v-for="(poiType, index) in visiblePois"
      ref="visiblePoisMarkers"
    >
      <l-marker
        v-for="(poi, index) in poiType"
        :key="poi.ref"
        :lat-lng="poi.latlng"
        :options="poi.options"
        :icon="getPoiIcon(poi)"
        @click="selectPoi(poi)"
      >
        <l-popup class="leafletPopup leafletPopup-poi">
          <div>
            <h5 class="poiTitle">{{ poi.name }}</h5>
            <p v-if="!useRdds" class="poiDescription" v-html="poi.description"></p>
            <div v-else class="poiInfos">
              <div class="poiAdress" v-html="poi.adress"></div>
              <div>
                <div class="linesLabel">{{ $t("linesnearby") }}</div>
                <div class="linesList" role="list" data-height-clamp="32px">
                  <span
                    v-for="(line, lineIdx) in poi.lines"
                    :key="lineIdx"
                    class="nbLigne u3 tip"
                    role="listitem"
                    :title="$t('viewTheRoute')"
                    :style="`background : ${getServiceBgColor(
                      line
                    )}; color:${getServiceColor(line)}`"
                    @click="selectLine(line.ref, null)"
                  >
                    {{ line?.mnemo }}
                </span>
              </div>
              </div>
              <a class="button button--primary poiLink" :href="poi.link">{{ $t("view") }}</a>
            </div>
          </div>
        </l-popup>
      </l-marker>
    </l-feature-group>
    <l-marker
      v-for="(stop, index) in visibleStops"
      :key="stop.ref"
      :ref="stop.ref"
      :lat-lng="stop.latlng"
      :options="stop.options"
      @click="selectStop(stop); preventCloseStopPopup(stop, $event);"
      @popupopen="onStopPopupOpen($event)"
      @popupclose="onStopPopupClose($event)"
    >
      <l-popup class="leafletPopup" :ref="'popup-' + stop.ref">
        <!-- TODO: offset 0, -35 -->
        <div>
          <p class="stopNameLabel">
            {{ stop.name }}
            <span v-if="showStopId" class="stopId">({{ stop.ref }})</span>
          </p>
          <div v-if="useRdds" class="linesLabel">{{ $t("linesofthestop") }}</div>
          <div class="linesList" role="list" data-height-clamp="32px">
            <span
              v-for="(line, lineIdx) in stop.lines"
              :key="lineIdx"
              class="nbLigne u3 tip"
              role="listitem"
              tabindex="0"
              :title="$t('viewTheRoute')"
              :style="`background : ${getServiceBgColor(
                line
              )}; color:${getServiceColor(line)}`"
              @click="selectLine(line.ref, stop.ref)"
              @keyup.enter="selectLine(line.ref, stop.ref)"
            >
              {{ line.mnemo }}
            </span>
          </div>
        </div>
      </l-popup>
      <l-icon
        v-if="stop.isSelected"
        v-bind="stopMarkerProperties"
      >
        <mapMarkerSVG color="var(--rd-map-marker-color-selected)"></mapMarkerSVG>
      </l-icon>
      <l-icon
        v-else
        class="mycap"
        v-bind="stopMarkerProperties"
      >
        <mapMarkerSVG :color="stop.colorForSelectedLine"></mapMarkerSVG>
      </l-icon>
    </l-marker>

    <l-marker
      v-for="(bus, index) in visibleBuses"
      :key="`bus_${index}`"
      :lat-lng="bus.latlng"
      :z-index-offset="10000"
    >
      <l-popup class="leafletPopup">
        <!-- TODO: offset 0, -35 -->
        <div>
          <p class="lineTitle">
            <span :style="'padding:0.2em;color:#FFF;background-color:'+bus.color">
              {{ bus.line }}
            </span> {{ bus.headsign }}<span v-if="showBearing"> [{{bus.bearing}}]</span>
          </p>
        </div>
      </l-popup>
      <l-icon :icon-size="[56, 56]">
        <div :style="{ '--rd-transport-marker-color-bearing': bus.color, '--rd-transport-marker-color-vehicle': bus.color}">
          <mapMarkerTransportSVG
            :vehicleType="getVehiculeLabelType(bus.type)"
            :bearing="bus.bearing"
          ></mapMarkerTransportSVG>
        </div>
      </l-icon>
    </l-marker>
  </l-map>
</template>

<script>
import { latLng } from "leaflet";
import { BackgroundImageIcon } from "../lib/Leaflet.backgroundImageIcon.styles.ts";
// Leaflet components
import {
  LMap,
  LTileLayer,
  LMarker,
  LIcon,
  LPopup,
  LPolyline,
  LFeatureGroup,
  LControlZoom,
} from "@vue-leaflet/vue-leaflet";

// Other components
import mapMarkerSVG from "./svg/mapMarkerSVG";
import mapMarkerTransportSVG from "./svg/mapMarkerTransportSVG";

// Mixins
import MapBaseMixin from "../mixins/maps/MapBaseMixin";

// Other components
import mapGeolocMarkerSVG from "./svg/mapGeolocMarkerSVG";

/// Static images import
/// Need that to have images in compiled component
import poiMarker from "../assets/poi-marker.svg";
import z15icon from "../assets/z15.png";
import z16icon from "../assets/z16.png";
import z17icon from "../assets/z17.png";

export default {
  name: "ApthMap",
  inject: ['siteBasePath'],
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LIcon,
    LPopup,
    LPolyline,
    LFeatureGroup,
    LControlZoom,
    mapMarkerSVG,
    mapMarkerTransportSVG,
    mapGeolocMarkerSVG,
  },
  mixins: [MapBaseMixin],
  data() {
    return {
      userPosition: null,
      previousClickedIndex: null,
      popupOpen: [],
    };
  },
  props: {
    showStopId: {
      type: Boolean,
      required: false,
      default: true,
    },
    showBearing: {
      type: Boolean,
      required: false,
      default: true,
    },
    stop: {
      type: Object,
      required: true,
      default: () => {
        return { lat: 40, lng: 0 };
      },
    },
    stopIdToOpen: {
      type: String,
      required: false,
      default: null,
    },
    line: {
      type: String,
      required: false,
      default: () => null,
    },
    stops: {
      type: Object,
      required: true,
      default: () => {
        return [];
      },
    },
    buses: {
      type: Array,
      required: false,
      default: () => {
        return [];
      },
    },
    lines: {
      type: Object,
      required: true,
      default: () => {
        return {};
      },
    },
    pois: {
      type: Object,
      required: false,
      default: () => {
        return [];
      },
    },
    allPoisTypes: {
      type: Array,
      required: false,
      default: () => {
        return [];
      },
    },
    shapes: {
      type: Array,
      required: true,
      default: () => {
        return [];
      },
    },
    shapesLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
    assetspath: {
      type: String,
      required: false,
      default: () => {
        return "";
      },
    },
    geolocUserTimestamp: {
      type: Number,
      required: false,
      default: 0,
    },
  },
  watch: {
    stop: function (stop) {
      let initialPos = latLng(stop.lat, stop.lng);
      this.center = initialPos;
    },
    geolocUserTimestamp: function (timestamp) {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((pos) => {
          let userPosition = latLng(
            pos.coords.latitude,
            pos.coords.longitude,
          );
          this.center = userPosition;
          this.userPosition = userPosition;
        });
      }
    },
    stopIdToOpen: function (stopId) {
      // When stopIdToOpen change and map is ready
      // Open the popup
      // If geoloc has been used, don't auto open the popup : it will recenter the map and we lose the user position
      this.$nextTick(() => {
        if (
            this.userPosition == null &&
            this.$refs[stopId] &&
            this.$refs[stopId][0]
          ) {
          this.$refs[stopId][0].leafletObject.openPopup();
          // Simulate previous click on this marker to keep popup opened on second click
          let index = `popup-${stopId}`;
          this.previousClickedIndex = index;
        }
      });
    },
  },
  computed: {
    selectedLine() {
      if (this.line) {
        return this.lines[this.line];
      }
      return null;
    },
    pedestrianDistance() {
      let size = [0, 0];
      let image = "";
      switch (this.currentZoom) {
        case 15:
          image = z15icon;
          size = [483, 483];
          break;
        case 16:
          image = z16icon;
          size = [937, 937];
          break;
        case 17:
          image = z17icon;
          size = [1843, 1843];
          break;
      }
      let options = {
        // Disable all interactions with pedestrian distance marker
        interactive: false,
        keyboard: false,
      };
      return { size, image, options };
    },
    visibleBuses() {
      let markers = [];
      //we limit the points to the visible portion of the map
      let buses = this.filterMarkersByBounds(this.buses, this.bounds);
      for (let busId of Object.keys(buses)) {
        markers.push(this.getMarkerByBus(buses[busId]));
      }
      return markers;
    },
    visibleStops() {
      let markers = [];
      // For readability and perfs, we don't display points on big maps
      if (this.zoomMin < this.currentZoom) {
        //...and we limit the points to the visible portion of the map
        let busStops = this.filterMarkersByBounds(this.stops, this.bounds);
        for (let busStopId of Object.keys(busStops)) {
          let busStop = busStops[busStopId];
          if (busStop.isCommercial && busStop.spoints.length > 0) {
            for (let spoint of busStop.spoints) {
              markers.push(this.getMarkerByStop(spoint, busStop.ref));
            }
          } else {
            markers.push(this.getMarkerByStop(busStop));
          }
        }
      }
      return markers;
    },
    visiblePois() {
      let visiblePois = [];
      // Dont display POIs for small zoom values
      if (this.zoomMin < this.currentZoom) {
        for (let poiTypeId of Object.keys(this.pois)) {
          let dataValue = this.pois[poiTypeId];
          // Get the type data
          let typeValue = null;
          for (let index of Object.keys(this.allPoisTypes)) {
            const dataTypeValue = this.allPoisTypes[index];
            if (dataTypeValue.tid == poiTypeId) {
              typeValue = dataTypeValue;
            }
          }
          // Limit the points to the visible part of the map
          dataValue = this.filterMarkersByBounds(dataValue, this.bounds);
          visiblePois[poiTypeId] = this.getPoisByType(dataValue, typeValue);
        }
      }
      return visiblePois;
    },
  },
  methods: {
    onStopPopupOpen($event) {
      this.$nextTick(() => {
        var popupWrapper = $event?.target?._popup?._wrapper;
        popupWrapper?.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')?.focus();
      });
    },
    onStopPopupClose($event) {
      this.$nextTick(() => {
        $event?.target?._icon?.focus();
      });
    },
    initialPos() {
      return latLng(this.stop.lat, this.stop.lng);
    },
    preventCloseStopPopup: function(stop, $event) {
      let index = `popup-${stop.ref}`;
      if (this.previousClickedIndex === index) {
        this.$nextTick(() => {
          // Reopen popup if the same marker is clicked
          // We can't avoid auto closing popup on second click on the same marker
          // So we have to reopen the popup
          $event.target.openPopup();
        });
      }
      this.previousClickedIndex = index;
    },
    selectStop: function (stop) {
      this.$emit("selectstop", stop);
    },
    selectLine: function (line, stop) {
      this.$emit("selectline", line, stop);
    },
    zoomUpdate(zoom) {
      this.currentZoom = zoom;
    },
    getMarkerByStop(busStop, parentRef) {
      if (typeof parentRef === "undefined") {
        parentRef = busStop.ref;
      }
      let isSelected = busStop.x == this.stop.lat && busStop.y == this.stop.lng;
      let onSelectedLine = this.line && busStop.lines.includes(this.line);
      let colorForSelectedLine = onSelectedLine
        ? this.selectedLine.colors.bgcolor
        : "var(--rd-map-marker-color)";
      let mark = {
        ref: busStop.ref,
        parentRef,
        isSelected,
        colorForSelectedLine,
        name: busStop.name,
        lines: busStop.lines.map((lineId) => this.lines[lineId]),
        latlng: latLng(busStop.x, busStop.y),
        options: {
          riseOnHover: true,
          riseOffset: 950,
          zIndexOffset: 900,
          title: busStop.name,
        },
      };
      return mark;
    },
    getMarkerByBus(bus) {
      let mark = {
        latlng: latLng(bus.x, bus.y),
        bearing: bus.bearing,
        line: bus.mnemo,
        headsign: bus.headsign,
        color: "#"+bus.color,
        type: bus.type,
        opts: {
          riseOnHover: true,
          riseOffset: 950,
          zIndexOffset: 900,
        },
      };
      return mark;
    },
    filterMarkersByBounds(markers, bounds) {
      var result = [];
      if (bounds) {
        for (let i in markers) {
          let marker = markers[i];
          if (bounds.contains(latLng(marker.x, marker.y))) {
            result.push(marker);
          }
        }
      }
      return result;
    },
    // Build complete POIs marker values
    getPoisByType(pois, poiType) {
      let markers = [];
      for (let poiId of Object.keys(pois)) {
        const dataValue = pois[poiId];
        const poiAdress = (dataValue.adress ? `${dataValue.adress}, ` : '') +
          (dataValue.zipcode ? `${dataValue.zipcode} ` : '') + (dataValue.city ?? '');
        let marker = {
          ref: dataValue.ref,
          name: dataValue.name,
          tid: dataValue.field_type_poi,
          link: dataValue.link,
          latlng: latLng(dataValue.x, dataValue.y),
          description: dataValue.description,
          lines: (this.useRdds && dataValue.lines) ? dataValue.lines.map((lineId) => this.lines[lineId]).filter((line) => line !== undefined) : null,
          adress: poiAdress,
          typeIcon: poiType.icon,
          typeIconUri: poiType.iconUri,
          typeClass: poiType.class,
          typeMarker: poiType.marker,
          options: {
            title: dataValue.name,
            riseOnHover: true,
            riseOffset: 950,
            zIndexOffset: 900,
          },
        };
        markers.push(marker);
      }
      return markers;
    },
    // Prepare a marker for a specific poi
    getPoiIcon(poi) {
      // If there is a icon URI, don't add a class
      const iconClass = (poi.typeIconUri) ? '' : poi.typeIcon;
      const poiIcon = new BackgroundImageIcon({
        iconSize: [32, 38],
        popupAnchor: [0, -23],
        className: `${iconClass} icon poi-type-marker ${poi.typeClass}`,
        pictoClasses: poi.typeClass,
        ...(poi.typeIconUri && { pictoUrl: poi.typeIconUri }),
        // Remove that
        // ...(poi.typeIconUri && { pictoUrl: `${this.siteBasePath}${poi.typeIconUri}` }), // TEST local VueJS
        // poiMarker : default marker shipped with the compoment
        iconUrl: poi.typeMarker
          ? `${this.siteBasePath}${poi.typeMarker}`
          : poiMarker,
      });
      return poiIcon;
    },
    selectPoi(poi) {
      this.center = poi.latlng;
    },
  },
};
/*
  <path d="M32.62,25.79c1.82-2.73-3-5.24-10.71-5.61S6.43,21.74,4.62,24.47C3,26.85,6.46,29.06,12.49,29.83L13,33.57l5.31-3.46C25,29.93,31,28.16,32.62,25.79Z" transform="translate(12 7.59)" style="fill: gray;opacity: 0.5"/>
        <path d="M25,13A12,12,0,1,0,10.5,24.74a.5.5,0,0,1,.38.35L13,32.5l2.12-7.42a.5.5,0,0,1,.38-.35A12,12,0,0,0,25,13ZM13,21.59A8.59,8.59,0,1,1,21.59,13,8.6,8.6,0,0,1,13,21.59Z" transform="translate(12 7.59)" style="fill: {{colors.bgcolor}}"/>
        <circle cx="25" cy="20.59" r="7.59" style="fill: #FFF"/>
        <path d="M13,0a13,13,0,0,0-3,25.65l2.52,8.81a.5.5,0,0,0,1,0L16,25.65A13,13,0,0,0,13,0Zm2.5,24.74a.5.5,0,0,0-.38.35L13,32.5l-2.12-7.42a.5.5,0,0,0-.38-.35,12,12,0,1,1,5,0ZM13,4.41A8.59,8.59,0,1,0,21.59,13,8.6,8.6,0,0,0,13,4.41Zm0,16.18A7.59,7.59,0,1,1,20.59,13,7.6,7.6,0,0,1,13,20.59Z" transform="translate(12 7.59)" style="fill: #231f20"/>
*/
</script>

<style>
@import "../../node_modules/leaflet/dist/leaflet.css";
@import "../../node_modules/leaflet.fullscreen/Control.FullScreen.css";

.leaflet-fake-icon-image-2x {
  background-image: url(../../node_modules/leaflet/dist/images/marker-icon-2x.png);
}

.leaflet-fake-icon-shadow {
  background-image: url(../../node_modules/leaflet/dist/images/marker-shadow.png);
}

/*
 * Reset unused div icon leaflet core styles.
 */
 .leaflet-marker-icon.leaflet-div-icon {
  background: transparent;
  border: none;
}

.u3 {
  padding: 0px 4px;
  min-width: 24px;
  height: 24px;
  line-height: 24px;
}

.nbLigne {
  margin: 0.2rem;
  display: inline-block;
  text-align: center;
  vertical-align: middle;
  cursor: pointer;
  font-weight: bold;
}

.stopNameLabel {
  font-weight: bold;
}

.stopId {
  font-style: italic;
}

.poi-type-marker {
  background-color: var(--rd-surface-brand-auxiliary, #FFF);
  background-size: contain;
  background-repeat: no-repeat;
  filter: drop-shadow(0px 4px 6px rgba(0, 0, 0, 0.06)) drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.10));
}

</style>
